import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import ArrowLeft from '@/presentation/assets/svg/ArrowLeft.svg'
import Download from '@/presentation/assets/svg/Download.svg'
import useNavigateWithOrderCode, {
  ILocationState
} from '@/services/hooks/useNavigateWithOrderCode'
import { useDivergence } from '@/services/providers/DivergenceContext'
import { usePreview } from '@/services/providers/PreviewContext'

interface HeaderProps {
  previousRoute?: string
  title: string
  deleteDivergenceButton?: React.ReactNode
}

const Header: React.FC<HeaderProps> = ({
  previousRoute,
  title,
  deleteDivergenceButton
}) => {
  const { t } = useTranslation()

  const { reports } = useDivergence()
  const { thereIsAnImageBeingUploaded } = usePreview()

  const navigate = useNavigateWithOrderCode()

  const { state } = useLocation()
  const alteration = (state as ILocationState)?.alteration ?? false

  const downloadReportPdfLink = useMemo(() => {
    const fullInspectionTypeReport = reports.find(
      report => report.type === 'full_inspection'
    )

    if (fullInspectionTypeReport?.file) return fullInspectionTypeReport?.file

    const fullDamagesTypeReport = reports.find(
      report => report.type === 'full_damages' && report.tags === null
    )

    return fullDamagesTypeReport?.file
  }, [reports])

  const handleNavigateBack = () => {
    if (thereIsAnImageBeingUploaded) return

    previousRoute ? navigate(previousRoute, alteration) : history.back()
  }

  return (
    <header className="mb-8 flex w-full justify-center bg-white p-8 shadow-md">
      <div className="flex w-full max-w-[1304px] items-center justify-between gap-x-4">
        <img
          className={
            thereIsAnImageBeingUploaded
              ? 'cursor-not-allowed'
              : 'cursor-pointer'
          }
          src={ArrowLeft}
          onClick={() => handleNavigateBack()}
        />

        <span className="truncate text-xl text-gray-500">{title}</span>

        <div className="flex items-center justify-start gap-x-4">
          {deleteDivergenceButton}

          {downloadReportPdfLink && (
            <a
              className="flex items-center gap-x-2 text-gray-500"
              href={downloadReportPdfLink}
              title={t('DOWNLOAD_PDF', 'Download do PDF')}
              target="_blank"
              rel="noreferrer"
              download
            >
              <img src={Download} alt="Download icon" />
            </a>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
